import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getGatewayCreateUserDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/inputs/gateway-create-user.form-default-values';
import { GatewayCreateUserModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/inputs/gateway-create-user.model';
import { removeEmptyFields } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/remove-empty-fields/remove-empty-fields';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { RoleListContextProvider } from '@app/feature/role/role.context';
import { UserCreateFormController } from '@app/feature/user/user-create/user-create-form/user-create-form.controller';
import { CreateUserContextProvider } from '@app/feature/user/user.context';
import { useCpmsBoCreateUserMutation } from '@service/cpms-bo-user.service';

interface UserCreateControllerProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export const UserCreateController = ({
  onSuccess,
  onCancel,
}: UserCreateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [createUserMutation] = useCpmsBoCreateUserMutation();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<GatewayCreateUserModel> = (errors) => {
    Logger.error(errors);
    toast.error(t('$.user-create.error', { error: t('global.unknown') }));
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<GatewayCreateUserModel> = (
    fieldValues,
  ) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const createUser = removeEmptyFields(fieldValues);

    createUserMutation(createUser)
      .unwrap()
      .then(() => {
        toast.success(t('$.user-create.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(t('$.user-create.error', { error: errorMessage }));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER //////////////////////////////////////////////////////////////////

  const data = getGatewayCreateUserDefaultValues();

  return (
    <CreateUserContextProvider data={data}>
      <RoleListContextProvider>
        <UserCreateFormController
          onError={handleError}
          onSubmit={handleSubmit}
          onCancel={onCancel}
          state={multiStepFormState}
        />
      </RoleListContextProvider>
    </CreateUserContextProvider>
  );
};
